import type { Upload, UploadWidget } from '@oneapp/shared';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '../components/icons';
import { Sheet } from '../components/Sheet';
import { Button, Callout } from '../components/ui';
import { fileLabel, importsApi } from '../imports/api';
import { ApiError } from '../lib/api';

/**
 * R5 · Your bank or UPI statement: chosen here (a PDF, Excel or CSV from net banking or a UPI app), it
 * goes straight to storage and through the safety checks; "Read statement" hands it to the chat, which
 * asks for its password if it has one. The notes say what happens to it: read once, deleted, never shared.
 */

type Refusal =
  | 'type'
  | 'xls'
  | 'size'
  | 'failed'
  | 'wrong_type'
  | 'old_excel'
  | 'infected'
  | 'scan_failed'
  | 'size_changed'
  | 'missing';

const REFUSALS: Record<string, Refusal> = {
  wrong_type: 'wrong_type',
  old_excel: 'old_excel',
  infected: 'infected',
  scan_failed: 'scan_failed',
  size: 'size_changed',
  missing: 'missing',
};

const ENDINGS = ['.pdf', '.xlsx', '.csv'];
const TYPES = ['application/pdf', 'text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];

type Stage = { kind: 'idle' } | { kind: 'sending'; file: File } | { kind: 'checking'; file: File };

/** The note icons the server may send, as the app's own. */
const NOTE_ICONS: Record<string, IconName> = { lock: 'lock', 'shield-check': 'shield-check' };

export function StatementSheet({
  widget,
  onClose,
  onUploaded,
}: {
  widget: UploadWidget;
  onClose: () => void;
  /** The file is up and passed the checks, and the person said "Read statement". */
  onUploaded: (upload: Upload) => void;
}) {
  const { t } = useTranslation();
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [ready, setReady] = useState<Upload | null>(null);
  const [refusal, setRefusal] = useState<Refusal | null>(null);
  const maxMb = widget.maxSizeMb ?? 5;
  const busy = stage.kind !== 'idle';

  const send = async (file: File) => {
    const name = file.name.toLowerCase();
    if (name.endsWith('.xls')) return setRefusal('xls');
    if (!ENDINGS.some((ending) => name.endsWith(ending))) return setRefusal('type');
    if (file.size > maxMb * 1024 * 1024) return setRefusal('size');
    if (file.size === 0) return setRefusal('failed');
    setRefusal(null);
    setReady(null);
    setStage({ kind: 'sending', file });
    try {
      const ticket = await importsApi.createUpload({
        purpose: 'bank_statement',
        fileName: file.name,
        contentType: file.type,
        size: file.size,
      });
      await importsApi.put(ticket, file);
      setStage({ kind: 'checking', file });
      const { upload } = await importsApi.complete(ticket.upload.id);
      if (upload.status === 'rejected') {
        setRefusal(REFUSALS[upload.rejectReason ?? ''] ?? 'failed');
        return;
      }
      setReady(upload);
    } catch (error) {
      const code = error instanceof ApiError ? error.problem?.code : undefined;
      setRefusal(code === 'FILE_TOO_BIG' ? 'size' : code === 'FILE_TYPE_NOT_ALLOWED' ? 'type' : 'failed');
    } finally {
      setStage({ kind: 'idle' });
      if (input.current) input.current.value = '';
    }
  };

  return (
    <Sheet
      open
      onClose={busy ? () => {} : onClose}
      label={widget.secureLabel ?? widget.title}
      secure
      footer={
        <Button icon="file-check" disabled={!ready} busy={busy} onClick={() => ready && onUploaded(ready)}>
          {widget.submitLabel ?? widget.cta}
        </Button>
      }
    >
      <h2 className="sheet__title">{widget.heading ?? widget.title}</h2>
      {widget.subtitle ? <p className="statement__subtitle muted">{widget.subtitle}</p> : null}
      {widget.intro ? <p className="sheet__text">{widget.intro}</p> : null}

      <input
        ref={input}
        id={inputId}
        type="file"
        className="visually-hidden"
        accept={[...widget.accept, ...TYPES].join(',')}
        disabled={busy}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) void send(file);
        }}
      />
      {ready || busy ? (
        <ul className="statement__files" aria-label={t('records.statement.choose')}>
          <li className="statement__file">
            <span className={`icon-tile${ready ? ' icon-tile--green' : ''}`}>
              <Icon name="file-text" size={20} />
            </span>
            <span className="statement__file-text">
              <span className="statement__file-name">
                {ready?.fileName ?? (stage.kind !== 'idle' ? stage.file.name : '')}
              </span>
              <span className="statement__file-meta" role="status">
                {ready
                  ? fileLabel(ready.fileName, ready.size)
                  : stage.kind === 'sending'
                    ? t('records.statement.sending')
                    : t('records.statement.checking')}
              </span>
            </span>
            {ready ? <Icon name="check-circle" size={20} /> : <span className="spinner" aria-hidden="true" />}
          </li>
          <li>
            <label htmlFor={inputId} className={`statement__file statement__file--choose${busy ? ' is-busy' : ''}`}>
              <span className="icon-tile icon-tile--gray">
                <Icon name="upload" size={20} />
              </span>
              <span className="statement__file-text">
                <span className="statement__file-name">{t('records.statement.another')}</span>
              </span>
              <Icon name="chevron-right" size={18} />
            </label>
          </li>
        </ul>
      ) : (
        <label htmlFor={inputId} className="dropzone">
          <span className="icon-tile icon-tile--large">
            <Icon name="upload" size={25} />
          </span>
          <span className="dropzone__title">{t('records.statement.choose')}</span>
          <span className="dropzone__hint">{t('records.statement.limits', { size: maxMb })}</span>
        </label>
      )}
      {refusal ? (
        <Callout tone="error" icon="alert-triangle" role="alert">
          {t(`records.statement.refused.${refusal}`, { size: maxMb })}
        </Callout>
      ) : null}
      {widget.notes?.map((note) => (
        <Callout key={note.text} icon={NOTE_ICONS[note.icon] ?? 'info'}>
          {note.text}
        </Callout>
      ))}
    </Sheet>
  );
}
